import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { usePermission } from '@/permissions/usePermission';
import { ApprovalRequestCategory } from '@/api/types/approvalRequests';
import { RequestsTab } from './RequestsTab';
import { HistoryTab } from './HistoryTab';
import { usePendingRequestsHub } from './usePendingRequestsHub';

export interface ApprovalRequestsTabsPageProps {
  category: ApprovalRequestCategory;
  title: string;
  // Stable key segment for react-query keys ('me' | 'providers').
  scope: string;
  // Route prefix for the two tab routes, e.g. '/User/DailyReconciliation/MatchingEngineRequests'.
  // Switching the in-page tab navigates to `${basePath}/Requests|History` so the sidebar
  // sub-item highlight follows the active tab (and vice-versa).
  basePath: string;
  viewRequestsPermission: string;
  viewHistoryPermission: string;
  approveRejectPermission: string;
  initialTab: 'requests' | 'history';
}

// Generic maker-checker tabs page shared by Matching Engine Requests + Providers
// Requests (mirrors legacy _PendingRequestsTabs.cshtml parameterised by category).
export function ApprovalRequestsTabsPage({
  category,
  title,
  scope,
  basePath,
  viewRequestsPermission,
  viewHistoryPermission,
  approveRejectPermission,
  initialTab,
}: ApprovalRequestsTabsPageProps) {
  const hasPermission = usePermission();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canViewRequests = hasPermission(viewRequestsPermission);
  const canViewHistory = hasPermission(viewHistoryPermission);
  const canApprove = hasPermission(approveRejectPermission);

  const requestsKey = useMemo(() => ['approval-requests', scope, 'requests'] as const, [scope]);
  const historyKey = useMemo(() => ['approval-requests', scope, 'history'] as const, [scope]);

  const tabs = useMemo<TabItem[]>(() => {
    const items: TabItem[] = [];
    if (canViewRequests) items.push({ key: 'requests', title: 'Requests' });
    if (canViewHistory) items.push({ key: 'history', title: 'History' });
    return items;
  }, [canViewRequests, canViewHistory]);

  const [active, setActive] = useState<'requests' | 'history'>(
    initialTab === 'requests' && !canViewRequests ? 'history' : initialTab,
  );

  // Route → page: when the URL tab changes (sidebar click / back-forward), follow it.
  useEffect(() => {
    setActive(initialTab);
  }, [initialTab]);

  useEffect(() => {
    // Fall back to the first available tab if the requested one is not permitted.
    if (active === 'requests' && !canViewRequests && canViewHistory) setActive('history');
    else if (active === 'history' && !canViewHistory && canViewRequests) setActive('requests');
  }, [active, canViewRequests, canViewHistory]);

  // Page → route: switching the in-page tab navigates so the sidebar highlight follows.
  function changeTab(k: 'requests' | 'history') {
    setActive(k);
    const suffix = k === 'requests' ? 'Requests' : 'History';
    navigate(`${basePath}/${suffix}`);
  }

  usePendingRequestsHub({
    enabled: canViewRequests || canViewHistory,
    onPendingCountChanged: () => {
      if (canViewRequests) void queryClient.invalidateQueries({ queryKey: requestsKey });
      if (canViewHistory) void queryClient.invalidateQueries({ queryKey: historyKey });
    },
  });

  const breadcrumb = ['Home', title, active === 'requests' ? 'Requests' : 'History'];

  return (
    <div className="approval-requests-page">
      <PageHeader title={title} breadcrumb={breadcrumb} />
      <div className="card custom-card mb-2">
        <div className="card-header p-0">
          <Tabs tabs={tabs} active={active} onChange={(k) => changeTab(k as 'requests' | 'history')} variant="underline" />
        </div>
        <div className="card-body">
          {active === 'requests' && canViewRequests && (
            <RequestsTab category={category} canApprove={canApprove} queryKey={requestsKey} />
          )}
          {active === 'history' && canViewHistory && (
            <HistoryTab category={category} queryKey={historyKey} />
          )}
        </div>
      </div>
    </div>
  );
}
